import {
  WebGLRenderer,
  Raycaster,
  Scene,
  Object3D,
  Vector2,
  Vector3,
  Color,
  DoubleSide,
  PerspectiveCamera,
  TextureLoader,
  SphereGeometry,
  MeshBasicMaterial,
  Mesh,
  LinearFilter,
  MathUtils,
  RepeatWrapping,
  Clock,
  Quaternion,
  MathUtils as ThreeMath,
} from 'three';

// import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';

import { isMobile } from 'react-device-detect';
import { gsap, Power1 } from 'gsap';
import { DeviceOrientationControls } from './utils/DeviceOrientationControls';

import DrawManager from './DrawManager';
// import Stats from 'stats.js';

const BACKGROUND_COLOR = '#fefefe';
const BACKGROUND_IMAGE = 'street-gallery-bts/assets/textures/background.jpg';
const EventEmitter = require('events');

// fix the : "Unable to preventDefault inside passive event listener invocation." error
let passiveEventMode = false;

class ThreeCanvas extends EventEmitter {
  constructor() {
    super();

    this.animate = this.animate.bind(this);

    // global props
    this.container = null;
    this.width = 0;
    this.height = 0;
    this.newSphereTextureUrl = '';

    // objects
    this.sphereRadius = 500;

    // camera
    this.defaultHeading = 0;
    this.defaultPitch = 0;
    this.heading = 0;
    this.pitch = 0;
    this.lon = 0;
    this.lat = 0;
    this.phi = 0;
    this.theta = 0;
    this.useFixedCamTarget = true;

    // for dragging
    this.allowDrawing = true;
    this.allowCameraDragging = true;
    this.onMouseDownPos = new Vector2(0.0, 0.0);
    this.onMouseDownLonLat = new Vector2(0.0, 0.0);

    //fix the : "Unable to preventDefault inside passive event listener invocation." error on mobile
    passiveEventMode = !isMobile;

    // gyro
    this.controls = null;
    this.orientationIsEnabled = false;

    // user actions states
    this.isDragging = false;
    this.hasClickedObject = false;
    this.isMouseMoving = false;
    this.isFocus = false;

    // scene states
    this.fboNeedsUpdate = true;
    this.autoUpdate = true;
    this.autoRotate = true;
    this.autoRotateSpeed = 0.001;
  }

  setupScene(container) {
    this.container = container;
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    const renderer = new WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
      stencil: true,
      alpha: true,
    });

    this.raycaster = new Raycaster();
    const scene = new Scene();
    scene.background = new Color(BACKGROUND_COLOR);

    const loader = new TextureLoader();
    loader.load(BACKGROUND_IMAGE, (texture) => {
      scene.background = texture;
    });

    this.cameraTarget = new Vector3(0, 0, 0);
    const camera = new PerspectiveCamera(
      isMobile ? 80 : 70,
      this.width / this.height,
      100,
      3000,
    );
    camera.target = new Vector3(0, 0, 0);

    scene.add(camera);

    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    container.appendChild(this.renderer.domElement);

    this.backgroundScene = new Scene();
    this.backgroundScene.background = new Color('white');

    this.drawingScene = new Scene();
    this.drawContainer = new Object3D();

    this.scene.add(this.drawContainer);

    this.drawManager = new DrawManager(
      this.drawContainer,
      this.raycaster,
      this.camera,
      () => {
        // update fbo once loaded
        this.updatePanoTexture();
      },
    );

    this.drawManager.setDrawMode(1);
    this.drawManager.addLayer();
    this.drawManager.enableMouse = false;

    this.addListeners();
    this.setInitialPositions();
    this.start();
  }

  initDeviceOrientation() {
    if (isMobile && !this.controls) {
      this.controls = new DeviceOrientationControls(this.camera);
      return;
    }

    if (isMobile && this.controls) {
      this.controls.disconnect();
      this.controls.dispose();
      this.controls = null;
    }
  }

  setInitialPositions() {
    this.camera.position.z = 1500;
  }

  setDefaultCamera(duration, delay, andMove = false) {
    const camTarget = this.getDefaultCameraTarget();
    gsap.to(this.cameraTarget, {
      x: andMove ? camTarget.x - 280 : camTarget.x,
      y: camTarget.y,
      z: camTarget.z,
      duration,
      delay,
      ease: Power1.out,
      onStart: () => {
        this.autoRotate = false;
        if (this.sphereContainer) {
          gsap.to(this.sphereContainer.rotation, {
            y: 0,
            duration,
            delay: 0,
            ease: Power1.out,
          });
        }
      },
      onComplete: andMove
        ? () => {
            gsap.to(this.cameraTarget, {
              x: camTarget.x,
              y: camTarget.y,
              z: camTarget.z,
              duration: 3,
              delay: 0.7,
              ease: 'power2.inOut',
              onComplete: () => {
                this.useFixedCamTarget = false;
                this.allowCameraDragging = true;
                this.allowDrawing = false;
              },
            });
          }
        : () => {
            this.useFixedCamTarget = false;
            this.allowCameraDragging = true;
            this.allowDrawing = false;
          },
    });
  }

  zoomToCenter(z, duration, delay = 0.5, onComplete = null, intro = false) {
    if (this.camera.position.z === z) {
      duration = 0.1;
      delay = 0.0;
    }

    gsap.to(this.camera.position, {
      z,
      duration,
      delay,
      ease: Power1.easeInOut,
      onComplete: () => {
        if (onComplete) onComplete();
      },
    });

    this.setDefaultCamera(duration, delay, intro);

    gsap.to(this, {
      autoRotateSpeed: 0.0001,
      duration,
      delay,
      ease: Power1.out,
    });
  }

  showPlanes(delay, duration) {
    this.drawManager.imageManager.showPlanes(delay, duration);
  }

  setCameraTargetPosition(z, delay = 0.5) {
    gsap.to(this.camera.position, {
      z,
      duration: 4,
      delay,
      ease: Power1.out,
    });
  }

  fadeOutSphere() {
    const colorMaterial = new Color(this.sphere.material.color.getHex());

    gsap.to(colorMaterial, {
      r: 88 / 255,
      g: 38 / 255,
      b: 115 / 255,
      duration: 1,
      ease: Power1.easeInOut,
      onUpdate: () => {
        this.sphere.material.color = colorMaterial;
      },
    });

    gsap.to(this.camera, {
      fov: isMobile ? 70 : 60,
      duration: 0.8,
      ease: Power1.out,
      onUpdate: () => {
        this.camera.updateProjectionMatrix();
      },
    });
  }

  fadeInSphere() {
    gsap.to(this.camera, {
      fov: isMobile ? 80 : 70,
      duration: 0.7,
      ease: Power1.out,
      onUpdate: () => {
        this.camera.updateProjectionMatrix();
      },
    });

    const colorMaterial = new Color(this.sphere.material.color.getHex());
    gsap.to(colorMaterial, {
      r: 1,
      g: 1,
      b: 1,
      duration: 1,
      ease: Power1.easeInOut,
      onUpdate: () => {
        this.sphere.material.color = colorMaterial;
      },
    });
  }

  prepareForLoading() {
    this.sphere.visible = false;
  }

  addListeners() {
    this.renderer.domElement.addEventListener(
      'mousedown',
      (event) => {
        this.isMousePressedInCanvas = true;
        this.touchClock = new Clock();
        this.touchClock.start();
        this.onMouseDownPos = this.getMousePos(event);
        if (this.drawManager.enableMouse) {
          this.onStartDrag(event);
          this.onMouseDownHandler(event);
        } else {
          this.onStartDrag(event);
        }
      },
      false,
    );
    this.renderer.domElement.addEventListener(
      'mouseleave',
      (event) => {
        this.onMouseLeaveHandler(event);
      },
      false,
    );

    this.renderer.domElement.addEventListener(
      'mousemove',
      (event) => {
        this.onMouseMoveHandler(event);
      },
      false,
    );

    window.addEventListener(
      'mouseup',
      (event) => {
        const currentMousePos = this.getMousePos(event);
        const distance = this.onMouseDownPos.distanceTo(currentMousePos);

        if (
          this.isMousePressedInCanvas &&
          !this.drawManager.enableMouse &&
          this.touchClock &&
          this.touchClock.getElapsedTime() < 0.2 &&
          distance < 20
        ) {
          this.onMouseDownHandler(event);
          this.onMouseReleaseHandler(event);
        } else {
          this.onMouseReleaseHandler(event);
        }
      },
      false,
    );

    this.renderer.domElement.addEventListener(
      'touchstart',
      (event) => {
        this.onMouseDownPos = this.getMousePos(event);
        this.touchClock = new Clock();
        this.touchClock.start();
        this.onStartDrag(event);
      },
      { passive: passiveEventMode },
    );
    this.renderer.domElement.addEventListener(
      'touchmove',
      (event) => {
        this.onMouseMoveHandler(event);
        event.preventDefault();
      },
      { passive: passiveEventMode },
    );

    window.addEventListener(
      'touchend',
      (event) => {
        const currentMousePos = this.getMousePos(event);
        const distance = this.onMouseDownPos.distanceTo(currentMousePos);
        if (
          (this.isMousePressedInCanvas && !this.isDragging) ||
          (this.touchClock &&
            this.touchClock.getElapsedTime() < 0.5 &&
            distance < 20)
        ) {
          this.onMouseDownHandler(event);
          this.onMouseReleaseHandler(event);
        } else {
          this.onMouseReleaseHandler(event);
        }
      },
      { passive: passiveEventMode },
    );

    document.addEventListener(
      'visibilitychange',
      (event) => {
        this.updatePanoTexture();
      },
      false,
    );

    this.drawManager.imageManager.on('focus', () => {
      this.fadeOutSphere();
      const focusImage = this.drawManager.imageManager.selectedImage;
      this.emit('focus', focusImage);

      this.isFocus = true;
    });

    this.drawManager.imageManager.on('unfocus', () => {
      this.fadeInSphere();
      this.emit('focus-end', null);
      this.isFocus = false;
    });
  }

  onMouseDownHandler(event) {
    // check if we have clicked an object
    const posInTexture = this.getMousePositionInTexture(event);

    if (this.allowCameraDragging) {
      this.hasClickedObject = this.drawManager.onMousePressed(posInTexture);
      this.onMouseDownLonLat = new Vector2(this.lon, this.lat);
    }

    this.emit('mousedown', posInTexture);
  }

  onStartDrag(event) {
    this.isMouseMoving = true;
    this.isMousePressed = true;

    // store initial position for camera dragging
    if (this.allowCameraDragging) {
      this.onMouseDownLonLat = new Vector2(this.lon, this.lat);
    }
  }

  onMouseMoveHandler(event) {
    if (this.isMousePressed) {
      this.isDragging = true;
    }

    const posInTexture = this.getMousePositionInTexture(event);
    const objectOver = this.drawManager.onMouseMove(posInTexture);
    this.emit('mousemove', { posInTexture, objectOver });

    // change cursor if we're hovering
    if (objectOver || this.isFocus) {
      this.renderer.domElement.style.cursor = 'pointer';
    } else {
      this.renderer.domElement.style.cursor = 'grab';
    }

    // if we're dragging an object
    if (
      this.hasClickedObject ||
      (this.allowDrawing && posInTexture && this.isDragging)
    ) {
      this.renderer.domElement.style.cursor = 'grabbing';
      this.drawManager.onMouseDrag(posInTexture);
    }

    // if we're dragging the camera
    if (
      !this.hasClickedObject &&
      this.allowCameraDragging &&
      this.isDragging &&
      !this.isFocus
    ) {
      this.renderer.domElement.style.cursor = 'grabbing';
      const mousePos = this.getMousePos(event);
      let blurRate = isMobile ? 0.15 : 0.1;
      if (this.isFocus) blurRate = 1;
      this.lon =
        (this.onMouseDownPos.x - mousePos.x) * blurRate +
        this.onMouseDownLonLat.x;
      this.lat =
        (this.onMouseDownPos.y - mousePos.y) * blurRate +
        this.onMouseDownLonLat.y;
    }
  }

  onMouseReleaseHandler(event) {
    this.posInTexture = null;
    this.hasClickedObject = false;
    this.isMouseMoving = false;
    this.isDragging = false;
    this.isMousePressed = false;
    this.isMousePressedInCanvas = false;

    this.emit('mouseup');

    const posInTexture = this.getMousePositionInTexture(event);

    if (posInTexture) {
      this.drawManager.onMouseUp(posInTexture);
    }
  }

  onMouseLeaveHandler() {
    this.isMousePressedInCanvas = false;
    this.emit('mouseleave', {});
  }

  getGalleryState() {
    return {
      data: this.drawManager.getSerialized(),
      camParams: {
        lon: this.lon,
        lat: this.lat,
      },
    };
  }

  addPanoSphere() {
    
    const nSegments = 60;
    const geo = new SphereGeometry(this.sphereRadius, nSegments, nSegments);
    const mat = new MeshBasicMaterial({
      color: '#ffffff',
      side: DoubleSide,
      wireframe: false,
      transparent: true,
      opacity: 0,
    });

    if (this.sphere) {
      if (this.sphere.material) {
        this.sphere.material.dispose();
        if (this.sphere.material.map) this.sphere.material.map.dispose();
      }
      this.sphereContainer.remove(this.sphere);
      this.scene.remove(this.sphereContainer);
    }

    this.sphereContainer = new Object3D();
    this.sphere = new Mesh(geo, mat);
    this.sphereContainer.add(this.sphere);
    this.scene.add(this.sphereContainer);
    this.drawManager.imageManager.setSphere(this.sphere);
  }

  // deprecated
  orient(yaw, tilt_yaw, tilt_pitch) {
    //yaw moves horizontally

    const RAD = Math.PI / 180;
    let q = new Quaternion();
    let t = new Quaternion();
    let i = new Quaternion();

    t.setFromAxisAngle(new Vector3(0, 1, 0), tilt_yaw * RAD);
    i.copy(t).inverse();
    q.copy(t);

    t.setFromAxisAngle(new Vector3(0, 0, 1), -tilt_pitch * RAD);
    q = q.multiply(t).multiply(i);

    t.setFromAxisAngle(new Vector3(0, 1, 0), yaw * RAD);
    q = q.multiply(t);

    q = new Quaternion(-q.x, -q.y, q.z, q.w);
    this.sphere.quaternion.copy(q);
    this.sphere.scale.x = -1;
    this.sphere.scale.y = -1;
    this.sphere.scale.z = 1;
  }

  orientHeading(heading, pitch, roll) {
    let q = new Quaternion();
    q.setFromAxisAngle(new Vector3(0, -1, 0), heading);
    q = new Quaternion(q.x, q.y, q.z, q.w);
    this.sphere.quaternion.copy(q);

    this.sphere.rotateZ(-pitch);
    this.sphere.rotateX(roll);
  }

  setUrlAsTexture(url, onComplete) {
    const loader = new TextureLoader();
    this.newSphereTextureUrl = url;
    // load a resource
    loader.load(
      // resource URL
      url,

      // onLoad callback
      (texture) => {
        // checking that we are on the selected gallery..
        if (texture.image.src !== this.newSphereTextureUrl) {
          return;
        }

        //this.canvasTexture = new Texture(canvas);


        this.canvasTexture = texture;
        this.canvasTexture.flipY = true;
        this.canvasTexture.wrapS = RepeatWrapping;
        this.canvasTexture.repeat.x = -1;
        this.canvasTexture.magFilter = LinearFilter;
        this.canvasTexture.minFilter = LinearFilter;
        this.sphere.material.map = this.canvasTexture;

        this.sphere.material.needsUpdate = true;
        this.scene.visible = true;

        gsap.to(this.sphere.material, {
          opacity: 1,
          duration: 2,
          delay: 0,
          ease: Power1.out,
        });

        onComplete();
      },

      // onProgress callback currently not supported
      undefined,

      // onError callback
      function (err) {
        console.error('An error happened.');
      },
    );
  }

  start() {
    if (!this.frameId) {
      this.frameId = requestAnimationFrame(this.animate);
    }
  }

  getMousePos(e) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mPos = this.getUniversalMousePos(e);
    const x = mPos.x - rect.left;
    const y = mPos.y - rect.top;
    return new Vector2(x, y);
  }

  updateRaycaster(event) {
    const mousePos = this.getMousePos(event);
    const mouseX = (mousePos.x / this.width) * 2 - 1;
    const mouseY = -(mousePos.y / this.height) * 2 + 1;
    this.raycaster.setFromCamera(new Vector2(mouseX, mouseY), this.camera);
  }

  getMousePositionInTexture(event) {
    if (!this.sphereContainer || !this.canvasTexture) return;

    this.updateRaycaster(event);
    const intersects = this.raycaster.intersectObjects(
      this.sphereContainer.children,
    );

    if (intersects.length > 0) {
      // need to flip y here
      intersects[0].uv.y = 1.0 - intersects[0].uv.y;

      this.posInTexture = new Vector2(intersects[0].uv.x, intersects[0].uv.y);

      const w = this.canvasTexture.image.width;
      const h = this.canvasTexture.image.height;

      const mPos = this.getMousePos(event);

      return {
        screenMouseX: mPos.x,
        screenMouseY: mPos.y,
        x: intersects[0].uv.x,
        y: intersects[0].uv.y,
        xCentered: intersects[0].uv.x * w - w * 0.5,
        yCentered: (1.0 - intersects[0].uv.y) * h - h * 0.5,
      };
    }
    return null;
  }

  getUniversalMousePos(e) {
    let x, y;
    if (e.type === 'touchstart') {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.type === 'touchmove') {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.type === 'touchend') {
      x = e.changedTouches[0].clientX;
      y = e.changedTouches[0].clientY;
    } else {
      x = e.clientX;
      y = e.clientY;
    }
    return new Vector2(x, y);
  }

  getDefaultCameraTarget() {
    this.lon = this.defaultHeading + this.heading;
    this.lat = this.defaultPitch + this.pitch;
    this.phi = MathUtils.degToRad(90 - this.lat);
    this.theta = MathUtils.degToRad(this.lon);

    const r = this.sphereRadius;
    const dr = this.allowCameraDragging;
    let x;
    let y;
    let z;
    x = r * Math.sin(this.phi) * Math.cos(this.theta);
    y = dr ? -r * Math.cos(this.phi) : r * Math.cos(this.phi);
    z = r * Math.sin(this.phi) * Math.sin(this.theta);

    return new Vector3(x, y, z);
  }

  vector3toLonLat(vector3) {
    vector3.normalize();
    let lng = -Math.atan2(-vector3.x, -vector3.z) - Math.PI / 2;

    if (lng < -Math.PI) lng += Math.PI * 2;
    let p = new Vector3(vector3.x, 0, vector3.z);
    p.normalize();
    let lat = Math.acos(p.dot(vector3));

    if (vector3.y < 0) {
      lat *= -1;
    }
    return [MathUtils.radToDeg(lng), -MathUtils.radToDeg(lat)];
  }

  renderScene() {
    if (this.allowCameraDragging) {
      this.lat = Math.max(-85, Math.min(85, this.lat));
      this.phi = MathUtils.degToRad(90 - this.lat);
      this.theta = MathUtils.degToRad(this.lon);
    } else {
      this.phi = MathUtils.degToRad(90 - this.lat);
      this.lon = this.defaultHeading + this.heading;
      this.lat = this.defaultPitch + this.pitch;
      this.theta = MathUtils.degToRad(this.lon);
    }

    const br = 0.6;
    const r = this.sphereRadius;
    const tr = this.camera.target;
    const dr = this.allowCameraDragging;

    let x;
    let y;
    let z;
    if (this.useFixedCamTarget) {
      x = this.cameraTarget.x;
      y = this.cameraTarget.y;
      z = this.cameraTarget.z;
    } else {
      x = r * Math.sin(this.phi) * Math.cos(this.theta);
      y = dr ? -r * Math.cos(this.phi) : r * Math.cos(this.phi);
      z = r * Math.sin(this.phi) * Math.sin(this.theta);
    }

    if (!this.orientationIsEnabled) {
      this.camera.target.x = br * tr.x + (1.0 - br) * x;
      this.camera.target.y = br * tr.y + (1.0 - br) * y;
      this.camera.target.z = br * tr.z + (1.0 - br) * z;
      this.camera.lookAt(this.camera.target);
    }

    // this.renderer.render(this.backgroundScene, this.camera);
    this.renderer.render(this.scene, this.camera);
  }

  updatePanoTexture() {
    if (this.sphere) this.sphere.visible = true;
    if (this.sphere && this.sphere.material && this.sphere.material.map)
      this.sphere.material.map.needsUpdate = true;
    this.fboNeedsUpdate = true;
  }

  detectDeviceOrientationChange() {
    const orientationIsEnabled =
      this.controls !== null && this.controls.isEnabled();

    if (orientationIsEnabled !== this.orientationIsEnabled) {
      this.orientationIsEnabled = orientationIsEnabled;
      this.emit('orientation', this.orientationIsEnabled);
    }
  }

  animate() {
    if (!this.autoUpdate || !this.scene.visible) return;
    this.detectDeviceOrientationChange();

    if (this.controls && this.controls.isEnabled() && !this.isFocus) {
      this.controls.update();
    }

    this.rotateSphere();
    this.renderScene();

    this.frameId = requestAnimationFrame(this.animate);
  }

  rotateSphere() {
    if (this.autoRotate && this.sphere) {
      this.sphereContainer.rotation.y += this.autoRotateSpeed;
      if (ThreeMath.radToDeg(this.sphereContainer.rotation.y) > 360) {
        this.sphereContainer.rotation.y = 0;
      }
    }
  }

  stop() {
    cancelAnimationFrame(this.frameId);
  }

  destroyContext() {
    if (this.renderer) {
      this.renderer.forceContextLoss();
      this.renderer.domElement = null;
      this.renderer = null;
    }
  }

  handleWindowResize() {
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
  }

  saveCanvas() {
    /*
    this.renderer.reserveDrawingBuffer = true;
    const id = this.db.currentEntityKey;
    this.renderScene();
   
    this.renderer.domElement.toBlob((blob) => {
      saveAs(blob, id + '.png');
      this.renderer.reserveDrawingBuffer = false;
    });
    */
  }
}

export default ThreeCanvas;
